import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * SessionTimeoutManager - watches for user activity across the whole app and, after a period of
 * inactivity, warns the user with a countdown before ending the session. Mounted once near the
 * app root (see App.tsx) so it applies regardless of which route is active.
 *
 * Timing is read from env.json so it can be tuned per deployment without a code change:
 *   SessionTimeoutMinutes - total idle time allowed before logout (default 20)
 *   SessionWarningSeconds - how long the warning dialog counts down for, carved out of the
 *                           tail end of that window (default 60; `SessionTimeoutWarningSeconds`
 *                           is accepted as an alias)
 *
 * The timer only warns while a signed-in token is present, so apps with anonymous pages (the CMS
 * portal) can mount it unconditionally. Logout itself is redirectToLogout (Utils/sessionRedirect).
 */
import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, LinearProgress } from '@mui/material';
import { getEnvVariable } from '../../../envs';
import { hasSessionToken, redirectToLogout } from '../Utils/sessionRedirect';
const DEFAULT_TIMEOUT_MINUTES = 20;
const DEFAULT_WARNING_SECONDS = 60;
// Activity that counts as "still here". Deliberately broad - any of these resets the idle clock.
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'scroll', 'wheel'];
// Activity resets the idle timer at most this often - without this, mousemove alone would fire
// the reset dozens of times a second.
const ACTIVITY_THROTTLE_MS = 1000;
const parsePositiveNumber = (value, fallback) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};
const SessionTimeoutManager = () => {
    const timeoutMinutes = parsePositiveNumber(getEnvVariable('SessionTimeoutMinutes'), DEFAULT_TIMEOUT_MINUTES);
    const warningSeconds = parsePositiveNumber(getEnvVariable('SessionWarningSeconds') ?? getEnvVariable('SessionTimeoutWarningSeconds'), DEFAULT_WARNING_SECONDS);
    const [warningOpen, setWarningOpen] = React.useState(false);
    const [secondsLeft, setSecondsLeft] = React.useState(warningSeconds);
    const idleTimerRef = React.useRef(null);
    const countdownIntervalRef = React.useRef(null);
    // Mirrors warningOpen for the activity listener, which otherwise closes over a stale value.
    const warningOpenRef = React.useRef(false);
    const lastActivityRef = React.useRef(0);
    const clearIdleTimer = () => {
        if (idleTimerRef.current) {
            clearTimeout(idleTimerRef.current);
            idleTimerRef.current = null;
        }
    };
    const clearCountdown = () => {
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
        }
    };
    const startCountdown = React.useCallback(() => {
        setSecondsLeft(warningSeconds);
        clearCountdown();
        countdownIntervalRef.current = setInterval(() => {
            setSecondsLeft((prev) => {
                if (prev <= 1) {
                    clearCountdown();
                    redirectToLogout();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    }, [warningSeconds]);
    // Total idle time before logout is `timeoutMinutes` - the warning just occupies the last
    // `warningSeconds` of that window rather than adding on top of it.
    const scheduleIdleTimer = React.useCallback(() => {
        clearIdleTimer();
        const delayMs = Math.max(1, timeoutMinutes * 60 - warningSeconds) * 1000;
        idleTimerRef.current = setTimeout(() => {
            // Nobody signed in (anonymous portal page) - nothing to expire. The next activity
            // re-arms the timer, so a sign-in that happens later is still covered.
            if (!hasSessionToken())
                return;
            warningOpenRef.current = true;
            setWarningOpen(true);
            startCountdown();
        }, delayMs);
    }, [timeoutMinutes, warningSeconds, startCountdown]);
    const handleStayLoggedIn = React.useCallback(() => {
        clearCountdown();
        warningOpenRef.current = false;
        setWarningOpen(false);
        scheduleIdleTimer();
    }, [scheduleIdleTimer]);
    React.useEffect(() => {
        const handleActivity = () => {
            // Once the warning is up, only the explicit "Stay logged in" button should dismiss
            // it - an incidental mouse twitch shouldn't silently cancel a security prompt.
            if (warningOpenRef.current)
                return;
            const now = Date.now();
            if (now - lastActivityRef.current < ACTIVITY_THROTTLE_MS)
                return;
            lastActivityRef.current = now;
            scheduleIdleTimer();
        };
        ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, handleActivity, { passive: true }));
        scheduleIdleTimer();
        return () => {
            ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, handleActivity));
            clearIdleTimer();
            clearCountdown();
        };
    }, [scheduleIdleTimer]);
    const progressValue = (secondsLeft / warningSeconds) * 100;
    return (_jsxs(Dialog, { open: warningOpen, onClose: handleStayLoggedIn, maxWidth: "xs", fullWidth: true, children: [_jsx(DialogTitle, { children: "Session about to expire" }), _jsxs(DialogContent, { children: [_jsxs(Typography, { variant: "body2", sx: { mb: 2 }, children: ["You've been inactive for a while. For your security, you'll be signed out in", ' ', _jsx("strong", { children: secondsLeft }), " second", secondsLeft === 1 ? '' : 's', " unless you stay active."] }), _jsx(LinearProgress, { variant: "determinate", value: progressValue, color: secondsLeft <= 10 ? 'error' : 'primary' })] }), _jsxs(DialogActions, { children: [_jsx(Button, { onClick: () => redirectToLogout(), color: "inherit", children: "Log out now" }), _jsx(Button, { onClick: handleStayLoggedIn, variant: "contained", autoFocus: true, children: "Stay logged in" })] })] }));
};
export default SessionTimeoutManager;
